<script>
    import { onMount, onDestroy } from 'svelte';
    import { Child } from 'pym.js';
    import Graphic from './Graphic.svelte';
    import meta from './data/meta.json';
    
    const pkg = require('../package.json');
    
    let pymChild;
    let width = 600;
    let containerElement;
    let lastSentHeight = 0;
    let heightTimeout;
    
    function handlePymResize(newWidth) {
      width = newWidth;
    }
    
    function sendHeight(newHeight) {
      if (pymChild && newHeight > 0) {
        // Only send if height changed significantly
        const heightDiff = Math.abs(newHeight - lastSentHeight);
        if (heightDiff > 5) {
          lastSentHeight = newHeight;
          pymChild.sendHeight();
        }
      }
    }
    
    function debouncedSendHeight(newHeight) {
      clearTimeout(heightTimeout);
      heightTimeout = setTimeout(() => {
        sendHeight(newHeight);
      }, 100);
    }
    
    onMount(() => {
      let resizeObserver;
      
      // Wait for CSS to be fully loaded before initializing pym
      const initializePym = () => {
        pymChild = new Child({ 
          renderCallback: handlePymResize, 
          id: pkg.name 
        });
        
        if (containerElement) {
          const containerWidth = containerElement.clientWidth;
          if (containerWidth && containerWidth !== width) {
            width = containerWidth;
          }
          
          // Use ResizeObserver instead of reactive binding
          resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
              const newHeight = entry.contentRect.height;
              debouncedSendHeight(newHeight);
            }
          });
          resizeObserver.observe(containerElement);
        }
      };
      
      if (process.env.NODE_ENV === 'development') {
        Object.keys(meta).forEach((id) => {
          const node = document.querySelector(`.${id}`);
          if (node) {
            node.innerHTML = meta[id];
          }
        });
        initializePym();
      } else {
        // In production/staging, wait for CSS to be truly loaded
        const waitForCSS = () => {
          // Check if CSS link exists in head
          const cssLinks = document.querySelectorAll('link[rel="stylesheet"]');
          const hasCSS = cssLinks.length > 0;
          
          if (hasCSS) {
            // Wait for all stylesheets to load
            const stylesheetsLoaded = Array.from(cssLinks).every(link => {
              return link.sheet !== null;
            });
            
            if (stylesheetsLoaded) {
              // Add extra delay for render
              setTimeout(initializePym, 300);
            } else {
              // Keep checking
              setTimeout(waitForCSS, 100);
            }
          } else {
            // No CSS links yet, or using inline styles - just initialize
            setTimeout(initializePym, 500);
          }
        };
        
        if (document.readyState === 'complete') {
          waitForCSS();
        } else {
          window.addEventListener('load', waitForCSS);
        }
      }
      
      return () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    });
    
    onDestroy(() => {
      clearTimeout(heightTimeout);
      if (pymChild) {
        pymChild.remove();
      }
    });
  </script>
  
  <!-- Container element -->
  <div bind:this={containerElement} class="app-container">
    <Graphic {width} />
  </div>
  
  <style>
    .app-container {
      width: 100%;
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
  </style>